import React from 'react';
import { ArrowRight, CheckCircle, Clock, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { useServiceContent } from '../hooks/use-service-content';
import { useSectionsContent } from '../hooks/use-sections-content';
import { getServiceSlug } from '../lib/service-utils';
import { useLanguage } from '../i18n/LanguageContext';

const Services = () => {
  const { services } = useServiceContent();
  const { sectionsContent } = useSectionsContent();
  const { tr, localizeText } = useLanguage();

  const faqs = Array.isArray(sectionsContent?.faqs) ? sectionsContent.faqs : [];
  const servicesPage = sectionsContent?.servicesPage || {};
  const copy = sectionsContent?.publicPageCopy || {};
  const text = (key, fallback) => copy[key] || fallback;

  const getIcon = (iconName) => {
    const icons = {
      Brain: () => (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
      Heart: () => (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
        </svg>
      ),
      Users: () => (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
    };
    const IconComponent = icons[iconName];
    return IconComponent ? <IconComponent /> : null;
  };

  const activeServices = Array.isArray(services)
    ? services.filter((service) => service?.isActive !== false)
    : [];
  const sessionDurations = activeServices.map((service) => localizeText(service.duration));

  return (
    <div className="min-h-screen bg-white">
      <section className="bg-gradient-to-br from-sage-600 to-earth-700 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-6">
            {localizeText(servicesPage.heroTitle) || tr('services.hero.title')}
          </h1>
          <p className="text-xl text-sage-100 max-w-3xl mx-auto leading-relaxed">
            {localizeText(servicesPage.heroSubtitle) || tr('services.hero.subtitle')}
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="space-y-16">
            {activeServices.map((service, index) => (
              <div key={service.id} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="relative rounded-2xl overflow-hidden shadow-xl group">
                    <img
                      src={service.image}
                      alt={localizeText(service.title)}
                      className="w-full h-96 object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-sage-900/60 to-transparent"></div>
                  </div>
                </div>

                <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-sage-100 rounded-full flex items-center justify-center text-sage-600">
                      {getIcon(service.icon)}
                    </div>
                    <Badge className="bg-sage-100 text-sage-700 hover:bg-sage-200">
                      {tr('services.badge.professionalService')}
                    </Badge>
                  </div>

                  <h2 className="text-3xl font-bold text-sage-900 mb-4">{localizeText(service.title)}</h2>
                  <p className="text-lg text-sage-700 mb-6 leading-relaxed">
                    {localizeText(service.fullDescription)}
                  </p>

                  <div className="mb-6">
                    <h3 className="font-semibold text-sage-900 mb-3">{tr('services.keyBenefits')}</h3>
                    <ul className="space-y-2">
                      {(Array.isArray(service.benefits) ? service.benefits : []).map((benefit, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sage-700">
                          <CheckCircle className="w-5 h-5 text-sage-500 mt-0.5 flex-shrink-0" />
                          <span>{localizeText(benefit)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                    <Card className="border-sage-200 bg-sage-50">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <MapPin className="w-4 h-4 text-sage-600" />
                          <span className="font-semibold text-sage-900 text-sm">{tr('services.delivery')}</span>
                        </div>
                        <div className="space-y-1">
                          {(Array.isArray(service.deliveryMethods) ? service.deliveryMethods : []).map((method, idx) => (
                            <p key={idx} className="text-xs text-sage-700">{localizeText(method)}</p>
                          ))}
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="border-sage-200 bg-sage-50">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Clock className="w-4 h-4 text-sage-600" />
                          <span className="font-semibold text-sage-900 text-sm">{tr('services.duration')}</span>
                        </div>
                        <p className="text-xs text-sage-700">{localizeText(service.duration)}</p>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <Link to={`/services/${getServiceSlug(service, localizeText)}`}>
                      <Button variant="outline" className="border-sage-300 text-sage-700 hover:bg-sage-50">
                        {tr('action.learnMore')}
                      </Button>
                    </Link>
                    <Link to={`/booking?service=${encodeURIComponent(getServiceSlug(service, localizeText))}`}>
                      <Button className="bg-sage-600 hover:bg-sage-700 text-white shadow-md hover:shadow-lg transition-all duration-200 group">
                        {tr('services.bookThisService')}
                        <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-sage-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-sage-900 mb-12 text-center">
            {localizeText(servicesPage.comparisonTitle) || tr('services.comparison.title')}
          </h2>
          <Card className="border-sage-200 shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-sage-100">
                  <tr>
                    <th className="px-6 py-4 text-left text-sage-900 font-semibold">{text('comparisonFeature', tr('services.comparison.feature'))}</th>
                    <th className="px-6 py-4 text-center text-sage-900 font-semibold">{text('comparisonCoaching', tr('services.comparison.coaching'))}</th>
                    <th className="px-6 py-4 text-center text-sage-900 font-semibold">{text('comparisonMassage', tr('services.comparison.massage'))}</th>
                    <th className="px-6 py-4 text-center text-sage-900 font-semibold">{text('comparisonWorkshops', tr('services.comparison.workshops'))}</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-sage-200">
                  <tr>
                    <td className="px-6 py-4 text-sage-700 font-medium">{text('comparisonOnlineAvailable', tr('services.comparison.onlineAvailable'))}</td>
                    <td className="px-6 py-4 text-center">
                      <CheckCircle className="w-5 h-5 text-sage-500 mx-auto" />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-sage-400">—</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <CheckCircle className="w-5 h-5 text-sage-500 mx-auto" />
                    </td>
                  </tr>
                  <tr className="bg-sage-50">
                    <td className="px-6 py-4 text-sage-700 font-medium">{text('comparisonInPerson', tr('services.comparison.inPerson'))}</td>
                    <td className="px-6 py-4 text-center">
                      <CheckCircle className="w-5 h-5 text-sage-500 mx-auto" />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <CheckCircle className="w-5 h-5 text-sage-500 mx-auto" />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <CheckCircle className="w-5 h-5 text-sage-500 mx-auto" />
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-sage-700 font-medium">{text('comparisonSessionLength', tr('services.comparison.sessionLength'))}</td>
                    <td className="px-6 py-4 text-center text-sage-700 text-sm">{sessionDurations[0] || '60-90 min'}</td>
                    <td className="px-6 py-4 text-center text-sage-700 text-sm">{sessionDurations[1] || '60-90 min'}</td>
                    <td className="px-6 py-4 text-center text-sage-700 text-sm">{sessionDurations[2] || '2-4 hours'}</td>
                  </tr>
                  <tr className="bg-sage-50">
                    <td className="px-6 py-4 text-sage-700 font-medium">{text('comparisonGroupSessions', tr('services.comparison.groupSessions'))}</td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-sage-400">—</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-sage-400">—</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <CheckCircle className="w-5 h-5 text-sage-500 mx-auto" />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-sage-900 mb-12 text-center">
            {localizeText(servicesPage.faqTitle) || tr('services.faqTitle')}
          </h2>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="border border-sage-200 rounded-lg px-6 bg-white shadow-sm hover:shadow-md transition-shadow">
                <AccordionTrigger className="text-left font-semibold text-sage-900 hover:text-sage-700">
                  {localizeText(faq.question)}
                </AccordionTrigger>
                <AccordionContent className="text-sage-700 leading-relaxed">
                  {localizeText(faq.answer)}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-sage-600 to-earth-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">
            {localizeText(servicesPage.ctaTitle) || tr('services.cta.title')}
          </h2>
          <p className="text-xl text-sage-100 mb-8 max-w-2xl mx-auto leading-relaxed">
            {localizeText(servicesPage.ctaSubtitle) || tr('services.cta.subtitle')}
          </p>
          <Link to="/booking">
            <Button size="lg" className="bg-white text-sage-700 hover:bg-sage-50 shadow-xl hover:shadow-2xl transition-all duration-300 text-lg px-8 group">
              {localizeText(servicesPage.ctaButton) || tr('services.cta.button')}
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
